/**
 * Hook for managing chat message state and operations.
 */

import { useState, useCallback, useRef } from 'react';
import type { ChatMessage } from '../types';
import type { C1Component } from '../types/c1Types';
import { createErrorMessage } from '../utils/messageHelpers';
import { getComponentId } from '../utils/componentDeduplication';

export interface UseChatMessagesReturn {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  addMessage: (message: ChatMessage) => void;
  addErrorMessage: (errorMessage: string) => void;
  clearMessages: () => void;
  renderedComponentIds: React.MutableRefObject<Set<string>>;
  processedRequestIds: React.MutableRefObject<Set<string>>;
  processedTaskIds: React.MutableRefObject<Set<string>>;
  streamingMessageId: React.MutableRefObject<string | null>;
  pendingStateUpdate: React.MutableRefObject<Set<string>>;
}

/**
 * Hook for managing chat messages state and related refs.
 * 
 * @returns Message state and operations
 */
export function useChatMessages(): UseChatMessagesReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  
  // Track rendered component IDs to prevent duplicates
  const renderedComponentIdsRef = useRef<Set<string>>(new Set());
  
  // Track processed request IDs to prevent multiple COMPLETE processing
  const processedRequestIdsRef = useRef<Set<string>>(new Set());
  
  // Track processed task IDs to prevent duplicate processing from polling
  const processedTaskIdsRef = useRef<Set<string>>(new Set());
  
  // Track streaming message ID for progressive component rendering
  const streamingMessageIdRef = useRef<string | null>(null);
  
  // Track if we've already queued a state update for this request
  const pendingStateUpdateRef = useRef<Set<string>>(new Set());

  /**
   * Add a message to the chat.
   */
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  /**
   * Add an error message to the chat.
   */
  const addErrorMessage = useCallback((errorMessage: string) => {
    setMessages((prev) => [...prev, createErrorMessage(errorMessage)]);
  }, []);

  /**
   * Clear all messages and reset tracking refs.
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    renderedComponentIdsRef.current.clear();
    processedRequestIdsRef.current.clear();
    processedTaskIdsRef.current.clear();
    streamingMessageIdRef.current = null;
    pendingStateUpdateRef.current.clear();
  }, []);

  return {
    messages,
    setMessages,
    addMessage,
    addErrorMessage,
    clearMessages,
    renderedComponentIds: renderedComponentIdsRef,
    processedRequestIds: processedRequestIdsRef,
    processedTaskIds: processedTaskIdsRef,
    streamingMessageId: streamingMessageIdRef,
    pendingStateUpdate: pendingStateUpdateRef,
  };
}

